import { useState, useRef, useEffect, useCallback } from "react";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { decodeImage, canvasToBlob } from "./decode";
import { setJpegDpi } from "./jpegDpi";
import { sheetLayout, headFromAlpha, cropSourceSize, lowResolution } from "./passportMath";
import { looksLikeImage, notAnImageMessage } from "./imageCheck";
import { removeBackground } from "@/lib/backgroundRemoval";
import { NoticeBox } from "@/components/NoticeBox";
import { PASSPORT_PRESETS as SIZE_PRESETS, PASSPORT_DPI as DPI, mmToPx } from "./passportPresets";
import { PassportSpecNote } from "./PassportSpecNote";

const SIZE_LIMITS = [
  { id: "none", label: "No limit", bytes: 0 },
  { id: "50", label: "Under 50 KB", bytes: 50 * 1000 },
  { id: "100", label: "Under 100 KB", bytes: 100 * 1000 },
  { id: "240", label: "Under 240 KB", bytes: 240 * 1000 },
  { id: "500", label: "Under 500 KB", bytes: 500 * 1000 },
];

async function jpegUnder(canvas: HTMLCanvasElement, maxBytes: number): Promise<Blob> {
  if (!maxBytes) return canvasToBlob(canvas, "image/jpeg", 0.95);
  let lo = 0.2, hi = 0.95, best: Blob | null = null;
  for (let i = 0; i < 8; i++) {
    const q = (lo + hi) / 2;
    const b = await canvasToBlob(canvas, "image/jpeg", q);
    if (b.size <= maxBytes) { best = b; lo = q; } else hi = q;
  }
  return best ?? (await canvasToBlob(canvas, "image/jpeg", 0.2));
}

async function withDpi(blob: Blob): Promise<Blob> {
  return new Blob([setJpegDpi(new Uint8Array(await blob.arrayBuffer()), DPI) as BlobPart], { type: "image/jpeg" });
}

const TOOL_INFO_CONTENT = {
  what: `Resizes and crops a regular photo to standard passport/visa photo dimension templates for several countries, and can lay out multiple copies on a single printable sheet.`,
  how: `Upload a photo taken against a plain background, select a country's standard size template from the preset list, adjust the framing, and download either a single photo or a print-ready sheet.`,
  tip: `Take the original photo in even, natural light with a plain white or light-colored background — this tool handles the size and crop correctly, but it can't fix harsh shadows or a distracting background behind you.\n\nHonestly: these are standard dimension templates, not an official compliance check — actual government photo requirements can include rules this tool doesn't verify (specific background color, facial expression, glasses/head covering rules, recency of the photo, and more), and requirements do change. Always check your destination country's current official passport/visa photo requirements directly before submitting, rather than assuming a correctly-sized photo is automatically an accepted one.`,
};

export default function PassportPhoto() {
  const [src, setSrc] = useState<HTMLCanvasElement | null>(null);
  const [preset, setPreset] = useState(SIZE_PRESETS[0]);
  const [zoom, setZoom] = useState([100]);
  const [offsetX, setOffsetX] = useState([50]);
  const [offsetY, setOffsetY] = useState([30]);
  const [limit, setLimit] = useState(SIZE_LIMITS[0]);
  const [showGuide, setShowGuide] = useState(true);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [fileError, setFileError] = useState("");
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const originalFile = useRef<File | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (!looksLikeImage(file)) { setFileError(notAnImageMessage(file.name)); return; }
    setFileError("");
    decodeImage(file).then(({ canvas }) => { originalFile.current = file; setSrc(canvas); setNote(""); setZoom([100]); setOffsetX([50]); setOffsetY([30]); })
      .catch((e) => setFileError(e?.message || `"${file.name}" could not be opened as an image.`));
  }, []);

  const targetW = mmToPx(preset.widthMm);
  const targetH = mmToPx(preset.heightMm);

  const drawCrop = useCallback(
    (canvas: HTMLCanvasElement, w: number, h: number) => {
      if (!src) return;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, w, h);
      const targetRatio = w / h;
      const imgRatio = src.width / src.height;
      const scale = zoom[0] / 100;
      let baseW: number, baseH: number;
      if (imgRatio > targetRatio) { baseH = src.height; baseW = baseH * targetRatio; } else { baseW = src.width; baseH = baseW / targetRatio; }
      const cropW = baseW / scale;
      const cropH = baseH / scale;
      const maxX = src.width - cropW;
      const maxY = src.height - cropH;
      const srcX = Math.max(0, Math.min(maxX, (offsetX[0] / 100) * maxX));
      const srcY = Math.max(0, Math.min(maxY, (offsetY[0] / 100) * maxY));
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(src, srcX, srcY, cropW, cropH, 0, 0, w, h);
    },
    [src, zoom, offsetX, offsetY]
  );

  useEffect(() => {
    const c = previewCanvasRef.current;
    if (!c) return;
    drawCrop(c, 300, Math.round(300 * (targetH / targetW)));
    if (!showGuide || !src) return;
    // Guide (preview only, never in the download): the two ovals are the smallest and largest head size
    // allowed; the chin of the face should sit between their bottoms and the hair top between their tops.
    const ctx = c.getContext("2d")!;
    const w = c.width, h = c.height;
    ctx.save();
    ctx.lineWidth = 2;
    const topY = h * 0.1;
    for (const [frac, color, dash] of [[preset.headMax, "rgba(220,38,38,0.9)", [6, 4]], [preset.headMin, "rgba(22,163,74,0.95)", []]] as const) {
      const hh = h * frac;
      ctx.strokeStyle = color; ctx.setLineDash([...dash]);
      ctx.beginPath(); ctx.ellipse(w / 2, topY + hh / 2, hh * 0.36, hh / 2, 0, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.setLineDash([3, 3]); ctx.strokeStyle = "rgba(59,130,246,0.8)";
    ctx.beginPath(); ctx.moveTo(w / 2, 0); ctx.lineTo(w / 2, h); ctx.stroke();
    ctx.restore();
  }, [drawCrop, targetH, targetW, showGuide, preset, src]);

  const crop = src ? cropSourceSize(src.width, src.height, targetW, targetH, zoom[0]) : null;
  const res = crop ? lowResolution(crop.w, targetW) : null;

  const autoFit = async () => {
    if (!originalFile.current || busy) return;
    setBusy("Making the background white and fitting the head...");
    try {
      const url = await removeBackground(originalFile.current);
      const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
      const cut = document.createElement("canvas");
      cut.width = img.naturalWidth; cut.height = img.naturalHeight;
      cut.getContext("2d")!.drawImage(img, 0, 0);
      const alpha = cut.getContext("2d")!.getImageData(0, 0, cut.width, cut.height).data;
      const white = document.createElement("canvas");
      white.width = cut.width; white.height = cut.height;
      const wctx = white.getContext("2d")!;
      wctx.fillStyle = "#fff"; wctx.fillRect(0, 0, white.width, white.height);
      wctx.drawImage(cut, 0, 0);
      const head = headFromAlpha(alpha, cut.width, cut.height);
      setSrc(white);
      if (head) {
        const ratio = targetW / targetH;
        const imgRatio = white.width / white.height;
        const baseH = imgRatio > ratio ? white.height : white.width / ratio;
        const baseW = baseH * ratio;
        const mid = (preset.headMin + preset.headMax) / 2;
        let z = 1;
        if (head.headH) z = Math.min(4, Math.max(1, baseH / (head.headH / mid)));
        const cropH = baseH / z, cropW = baseW / z;
        const maxX = white.width - cropW, maxY = white.height - cropH;
        const sx = head.centerX - cropW / 2;
        const sy = head.top - cropH * 0.1;
        setZoom([Math.round(z * 100)]);
        setOffsetX([maxX > 0 ? Math.max(0, Math.min(100, (sx / maxX) * 100)) : 50]);
        setOffsetY([maxY > 0 ? Math.max(0, Math.min(100, (sy / maxY) * 100)) : 0]);
        setNote(head.headH ? "Background made white and the head sized to the middle of the allowed range. This is an estimate: check the face against the two guide ovals and adjust the sliders." : "Background made white and the head centred under the top margin. Head size could not be estimated: use Zoom so the face fits between the two guide ovals.");
      } else setNote("Background made white. No person was found to position automatically - use the sliders.");
    } catch (e) {
      toast.error("Could not make the background white: " + ((e as Error)?.message || "unknown error"));
    } finally { setBusy(""); }
  };

  const downloadSingle = async () => {
    if (busy) return;
    const canvas = document.createElement("canvas");
    drawCrop(canvas, targetW, targetH);
    const blob = await withDpi(await jpegUnder(canvas, limit.bytes));
    downloadBlob(blob, `passport-photo-${preset.id}.jpg`);
    toast.success(`${targetW} x ${targetH} px, ${DPI} DPI, ${(blob.size / 1000).toFixed(0)} KB`);
  };

  const downloadSheet = async () => {
    if (busy) return;
    const lay = sheetLayout(preset.widthMm, preset.heightMm);
    const sheetW = mmToPx(lay.sw), sheetH = mmToPx(lay.sh);
    const photoCanvas = document.createElement("canvas");
    drawCrop(photoCanvas, targetW, targetH);
    const sheet = document.createElement("canvas");
    sheet.width = sheetW; sheet.height = sheetH;
    const ctx = sheet.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, sheetW, sheetH);
    const totalW = lay.cols * targetW, totalH = lay.rows * targetH;
    const startX = (sheetW - totalW) / 2, startY = (sheetH - totalH) / 2;
    for (let r = 0; r < lay.rows; r++) for (let c = 0; c < lay.cols; c++) ctx.drawImage(photoCanvas, startX + c * targetW, startY + r * targetH, targetW, targetH);
    ctx.strokeStyle = "#bbb"; ctx.lineWidth = 1;
    for (let r = 0; r < lay.rows; r++) for (let c = 0; c < lay.cols; c++) ctx.strokeRect(startX + c * targetW, startY + r * targetH, targetW, targetH);
    const blob = await withDpi(await canvasToBlob(sheet, "image/jpeg", 0.95));
    downloadBlob(blob, `passport-photo-sheet-${preset.id}.jpg`);
    toast.success(`Print sheet ready: ${lay.count} photos on a 4x6 in sheet (${lay.sw > lay.sh ? "landscape" : "portrait"})`);
  };

  return (
    <ToolContainer>
      {!src ? (
        <>
        <FileDropZone gateAtDownload
          accept="image/*"
          onFiles={handleFiles}
          label="Drop a photo to create passport/visa photos"
          description="Face forward, plain background works best - processed entirely in your browser"
        />
        {fileError && <p role="alert" className="text-xs text-destructive mt-2" data-testid="passport-file-error">{fileError}</p>}
        </>
      ) : (
        <div className="space-y-5">
          <div>
            <Label>Photo Size</Label>
            <Select disabled={!!busy} value={preset.id} onValueChange={id => setPreset(SIZE_PRESETS.find(p => p.id === id) ?? SIZE_PRESETS[0])}>
              <SelectTrigger aria-label="Photo Size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SIZE_PRESETS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <PassportSpecNote preset={preset} limitLabel={limit.bytes ? limit.label : undefined} />
          </div>

          <div className="flex justify-center">
            <canvas ref={previewCanvasRef} className="border rounded-lg max-w-[300px] w-full" />
          </div>
          <label className="flex items-center gap-2 text-sm justify-center">
            <input type="checkbox" checked={showGuide} onChange={(e) => setShowGuide(e.target.checked)} /> Show head-size guide (green = smallest head, red dashed = largest; not in the download)
          </label>
          <div className="flex justify-center">
            <Button variant="outline" onClick={autoFit} disabled={!!busy}>{busy ? "Working…" : "Make background white + auto-fit head"}</Button>
          </div>
          <p role="status" aria-live="polite" className="text-xs text-center text-muted-foreground" data-testid="passport-busy">{busy ? `Working… ${busy} Download is paused until it finishes.` : ""}</p>
          {res?.low && (
            <NoticeBox role="alert" className="text-xs text-center" testId="passport-lowres">
              Low resolution: the part of your photo that fills the frame is only about {Math.round(crop!.w)} px wide, and this size needs {targetW} px for a sharp {DPI} dpi print. Passport offices reject blurry photos. Use a larger original, or zoom out.
            </NoticeBox>
          )}
          {note && <p className="text-xs text-muted-foreground text-center">{note}</p>}

          <div><Label>Zoom: {zoom[0]}%</Label><Slider aria-label="Zoom" value={zoom} onValueChange={setZoom} min={100} max={400} step={1} /></div>
          <div><Label>Position (horizontal)</Label><Slider aria-label="Position (horizontal)" value={offsetX} onValueChange={setOffsetX} min={0} max={100} step={1} /></div>
          <div><Label>Position (vertical)</Label><Slider aria-label="Position (vertical)" value={offsetY} onValueChange={setOffsetY} min={0} max={100} step={1} /></div>

          <div>
            <Label>File size limit (for online applications)</Label>
            <Select value={limit.id} onValueChange={id => setLimit(SIZE_LIMITS.find(l => l.id === id) ?? SIZE_LIMITS[0])}>
              <SelectTrigger className="w-48" aria-label="File size limit"><SelectValue /></SelectTrigger>
              <SelectContent>{SIZE_LIMITS.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="flex gap-3">
            <Button onClick={downloadSingle} variant="outline" className="flex-1" disabled={!!busy}>Download Single Photo</Button>
            <Button onClick={downloadSheet} className="flex-1" disabled={!!busy}>Download Print Sheet (4x6")</Button>
          </div>
          <Button variant="ghost" disabled={!!busy} onClick={() => { setSrc(null); originalFile.current = null; setFileError(""); }} className="w-full">
            Upload Different Photo
          </Button>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
